import React from 'react';
import { Settings2, Lock, RotateCcw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { availableColumns, toggleColumn, visibleColumns, isDefaultView } from '../lib/tableColumns';

// The gear icon in a table header: tick the columns to show. Every change is
// saved for the user straight away (see hooks/useTablePrefs.js).
const ColumnPicker = ({ table, saved, onChange, ctx = {}, title = 'Columns' }) => {
  const shown = new Set(visibleColumns(table, saved, ctx).map(c => c.key));
  const cols = availableColumns(table, ctx);
  const atDefault = isDefaultView(table, saved, ctx);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]"
          title="Choose columns"
          aria-label="Choose columns"
          data-testid={`column-picker-${table}`}
        >
          <Settings2 className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0 bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid={`column-picker-panel-${table}`}>
        <div className="px-3 py-2 border-b border-[#1E293B]">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-[#64748B]">Saved for you on every device</div>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {cols.map(c => (
            <label
              key={c.key}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm ${c.locked ? 'text-[#64748B] cursor-default' : 'text-[#F8FAFC] cursor-pointer hover:bg-[#1E293B]/60'}`}
              data-testid={`column-option-${table}-${c.key}`}
            >
              <input
                type="checkbox"
                className="accent-[#3B82F6]"
                checked={shown.has(c.key)}
                disabled={c.locked}
                onChange={() => onChange(toggleColumn(table, saved, c.key, ctx))}
              />
              <span className="flex-1">{c.label}</span>
              {c.locked && <Lock className="w-3 h-3" />}
            </label>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-[#1E293B] flex justify-end">
          <button
            type="button"
            disabled={atDefault}
            onClick={() => onChange(null)}
            className="inline-flex items-center gap-1 text-xs text-[#3B82F6] disabled:text-[#475569]"
            data-testid={`column-reset-${table}`}
          >
            <RotateCcw className="w-3 h-3" /> Reset to default
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnPicker;
